// Browser-side tunables for the Debug Panel — the live-test knobs that live in
// the browser (the segmenter's F10 thresholds and the bridge client's forward
// cadence). Persisted + live-read, mirroring the other pref modules
// (focusModal.ts / audioCues.ts). The gateway-side reflex knobs are separate
// (POST /bridge/config); these never leave the browser.
//
// Defaults MUST match the code defaults they override: segmenter.ts
// DEFAULT_SEGMENTATION and bridgeClient FLUSH_MS / TAIL_IDLE_MS — so an unset
// preference is byte-for-byte today's behavior.

export interface BrowserDebugParams {
  // Segmenter (F10) — see segmenter.ts SegmentationConfig.
  pauseMs: number
  minChars: number
  maxChars: number
  maxDurationMs: number
  // Bridge client forward cadence — see bridgeClient.ts.
  flushMs: number
  tailIdleMs: number
}

export const DEBUG_PARAM_DEFAULTS: BrowserDebugParams = {
  pauseMs: 3000,
  minChars: 140,
  maxChars: 320,
  maxDurationMs: 30000,
  flushMs: 600,
  tailIdleMs: 3200
}

const STORAGE_KEY = 'convariance.debug.params'

export function getBrowserDebugParams(): BrowserDebugParams {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return { ...DEBUG_PARAM_DEFAULTS }
    const parsed = JSON.parse(raw) as Partial<BrowserDebugParams>
    return { ...DEBUG_PARAM_DEFAULTS, ...parsed }
  } catch {
    return { ...DEBUG_PARAM_DEFAULTS }
  }
}

export function setBrowserDebugParams(partial: Partial<BrowserDebugParams>): BrowserDebugParams {
  const next = { ...getBrowserDebugParams(), ...partial }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  } catch {
    // private mode — keep nothing
  }
  return next
}

// --- panel visibility -------------------------------------------------------

const ENABLED_KEY = 'convariance.debug.panel'

/** Whether the Debug Panel is shown. Off by default; toggled on from the
 *  "Debug" button in the transcript footer (persisted across the session). */
export function isDebugPanelEnabled(): boolean {
  try {
    return localStorage.getItem(ENABLED_KEY) === '1'
  } catch {
    return false
  }
}

export function setDebugPanelEnabled(value: boolean): void {
  try {
    localStorage.setItem(ENABLED_KEY, value ? '1' : '0')
  } catch {
    // private mode — ignore
  }
}

/** The segmenter slice (passed to createSegmenter / segmenter.setConfig). */
export function getSegmentationParams(): {
  pauseMs: number
  minChars: number
  maxChars: number
  maxDurationMs: number
} {
  const p = getBrowserDebugParams()
  return {
    pauseMs: p.pauseMs,
    minChars: p.minChars,
    maxChars: p.maxChars,
    maxDurationMs: p.maxDurationMs
  }
}
